import swapApiService from '@/shared/services/swap-api.service'

import { checkNextPage } from '@/shared/lib/utils/pagination'

import {
  getCurrentTimestampInSeconds,
  getPreviousMonthFirstDayTimestamp,
} from '@/shared/lib/utils/date'

import { SWAP_LINKS_DISPATCHERS } from '@/shared/constants/store-constants/swapLinks'
import { TOAST_DISPATCHERS } from '@/shared/constants'
import i18n from '@/app/i18n'

export default {
  resetStore({ commit }) {
    commit('RESET_STORE')
  },

  resetPage({ commit }) {
    commit('RESET_PAGE')
  },

  _updateWidgetInTable({ state }, { id, formData }) {
    state.stats = state.stats.map((stat) =>
      stat.app === id ? { ...stat, app_name: formData.name } : stat
    )
  },

  onSelectSort({ commit, dispatch }, payload) {
    commit('RESET_PAGE')
    commit('SET_SORTING', payload)

    dispatch(SWAP_LINKS_DISPATCHERS.getSwapStats, {}, { root: true })
  },

  changePage({ commit, dispatch, state }) {
    if (checkNextPage(state.page, state.total, state.limit)) {
      commit('SET_PAGE', state.page + 1)

      dispatch(SWAP_LINKS_DISPATCHERS.getSwapStats, {}, { root: true })
    }
  },

  async createSwapApp({ commit, dispatch }, { appName, partnerId }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await swapApiService.createSwapApp(
        { name: appName },
        partnerId
      )

      commit('SET_APP_DATA', response)

      return { success: true, payload: response }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create swap app')

      dispatch(
        TOAST_DISPATCHERS.setErrorMessage,
        {
          title: i18n.global.t('ToastError | title'),
          message: i18n.global.t(
            error.response?.data &&
              error.response?.data.code &&
              error.response?.data.code === 'swap-partner-006'
              ? 'ToastError | message | unique | name'
              : 'ToastError | message'
          ),
        },
        { root: true }
      )

      return { success: false, error }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async getSwapApps({ commit }, partnerId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await swapApiService.getSwapApps(partnerId)

      commit('SET_APPS_LIST', response)
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to get swap apps')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getSwapStats({ state, commit }, params) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const extendedParams = {
        page: state.page,
        limit: state.limit,
        from: getPreviousMonthFirstDayTimestamp(),
        to: getCurrentTimestampInSeconds(),
        ...state.sorting,
        ...params,
      }

      const response = await swapApiService.getSwapStats(extendedParams)

      commit('SET_SWAP_STATS', response)
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to get swap stats')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateSwapApp(
    { commit, dispatch },
    { appName, requestData, partnerId, skipGetApps }
  ) {
    try {
      commit('SET_ACTION_LOADING', true)
      commit('SET_ERROR', null)

      const response = await swapApiService.updateSwapApp(appName, requestData)

      if (!skipGetApps) {
        await dispatch('getSwapApps', partnerId)
      }

      await dispatch(
        SWAP_LINKS_DISPATCHERS._updateWidgetInTable,
        {
          id: appName,
          formData: requestData,
        },
        { root: true }
      )

      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update swap app')
      throw error
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async deleteSwapApps({ commit, dispatch }, { appName, partnerId }) {
    try {
      commit('SET_ACTION_LOADING', true)
      commit('SET_ERROR', null)

      await swapApiService.deleteSwapApp(appName, partnerId)

      commit('RESET_PAGE')

      await dispatch(SWAP_LINKS_DISPATCHERS.getSwapStats, {}, { root: true })

      return true
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete swap app')
      throw error
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },
}
